import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  activate,
  fetchAndActivate,
  getBoolean,
  getRemoteConfig,
  getString,
  onConfigUpdate,
  type RemoteConfig,
} from 'firebase/remote-config';

import * as Sentry from '@sentry/react';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let firebaseApp: FirebaseApp | null = null;
let remoteConfig: RemoteConfig | null = null;
let remoteConfigInitialized = false;
let remoteConfigUpdateSubscribed = false;

const booleanFeatureDefaults = {
  chat: false,
  websocket_connect: true,
  repeat_claims: true,
  diagnosis_favourites: true,
  resubmit_tariff: true,
  report_income: true,
  report_custom: true,
  report_accounts_receivable: true,
  report_tariff_provider: true,
  report_provider_monthly: true,
  report_monthly_revenue_statement: true,
  report_provider_ytd: true,
  report_provider_grouping: true,
  action_management: true,
  provider_dashboard_accounts_receivable: false,
  provider_dashboard_maintenance: false,
  claim_table_next_page_prefetch: false,
  live_rules_apply_data: false,
  batch_claim_live_rules: false,
} as const;

const stringConfigDefaults = {
  dashboard_banner: JSON.stringify({
    enabled: false,
    title: 'Home page temporarily under maintenance',
    message: 'Thank you for your patience as we complete this update',
  }),
  provider_dashboard_banner: JSON.stringify({
    enabled: false,
    title: 'Home page temporarily under maintenance',
    message: 'Thank you for your patience as we complete this update',
  }),
  provider_dashboard_tooltips: JSON.stringify({}),
} as const;

const remoteConfigDefaults = {
  ...booleanFeatureDefaults,
  ...stringConfigDefaults,
} as const;

type FeatureName = keyof typeof booleanFeatureDefaults;
type StringConfigName = keyof typeof stringConfigDefaults;
type RemoteConfigListener = () => void;

const remoteConfigListeners = new Set<RemoteConfigListener>();

const notifyRemoteConfigListeners = () => {
  remoteConfigListeners.forEach((listener) => {
    listener();
  });
};

export const initFirebaseApp = (): FirebaseApp => {
  if (firebaseApp) return firebaseApp;

  firebaseApp = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  remoteConfig = getRemoteConfig(firebaseApp);

  if (!remoteConfigInitialized) {
    remoteConfigInitialized = true;
    remoteConfig.defaultConfig = remoteConfigDefaults;

    if (import.meta.env.DEV) {
      remoteConfig.settings = {
        fetchTimeoutMillis: 60000,
        minimumFetchIntervalMillis: 10000,
      };
    }

    void fetchAndActivate(remoteConfig).then(() => {
      notifyRemoteConfigListeners();
    });
    if (!remoteConfigUpdateSubscribed) {
      remoteConfigUpdateSubscribed = true;
      onConfigUpdate(remoteConfig, {
        next: () => {
          void activate(remoteConfig).then(() => {
            notifyRemoteConfigListeners();
          });
        },
        complete: () => {
          Sentry.captureMessage(
            'Remote config update stream completed unexpectedly'
          );
        },
        error: (error) => {
          Sentry.captureException(error);
        },
      });
    }
  }

  return firebaseApp;
};

export const getFirebaseApp = (): FirebaseApp => {
  return firebaseApp ?? initFirebaseApp();
};

export const getFirebaseRemoteConfig = (): RemoteConfig => {
  if (!remoteConfig) {
    initFirebaseApp();
  }

  return remoteConfig as RemoteConfig;
};

export const isWebSocketConnectEnabled = (): boolean => {
  const config = getFirebaseRemoteConfig();
  return getBoolean(config, 'websocket_connect');
};

export const isFeatureEnabled = (featureName: FeatureName): boolean => {
  const config = getFirebaseRemoteConfig();
  return getBoolean(config, featureName);
};

export const getStringConfigValue = (configName: StringConfigName): string => {
  const config = getFirebaseRemoteConfig();
  return getString(config, configName);
};

export const subscribeToRemoteConfigUpdates = (
  listener: RemoteConfigListener
): (() => void) => {
  remoteConfigListeners.add(listener);

  return () => {
    remoteConfigListeners.delete(listener);
  };
};
